import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useQuery } from '@tanstack/react-query';
import { Container, Table, Input, Segment, Header, Tab } from 'semantic-ui-react';
import { Link } from 'react-router-dom';
import { oils } from '../services/api';

const columnsByTab = {
  properties: [
    { key: 'sap', label: 'Sap', accessor: 'sap' },
    { key: 'bubbly', label: 'Bubbly', accessor: 'bubbly' },
    { key: 'cleansing', label: 'Cleansing', accessor: 'cleansing' },
    { key: 'condition', label: 'Condition', accessor: 'condition' },
    { key: 'hardness', label: 'Hardness', accessor: 'hardness' },
    { key: 'longevity', label: 'Longevity', accessor: 'longevity' },
    { key: 'stability', label: 'Stability', accessor: 'stability' },
  ],
  saturates: [
    { key: 'sap', label: 'Sap', accessor: 'sap' },
    { key: 'saturated', label: 'Saturated', accessor: 'saturated' },
    { key: 'mono_unsat', label: 'Mono-unsaturated %', accessor: 'mono_unsat' },
    { key: 'poly_unsat', label: 'Poly-unsaturated %', accessor: 'poly_unsat' },
  ],
  'fa-common': [
    { key: 'sap', label: 'Sap', accessor: 'sap' },
    { key: 'lauric', label: 'Lauric', accessor: 'lauric' },
    { key: 'linoleic', label: 'Linoleic', accessor: 'linoleic' },
    { key: 'linolenic', label: 'Linolenic', accessor: 'linolenic' },
    { key: 'myristic', label: 'Myristic', accessor: 'myristic' },
    { key: 'oleic', label: 'Oleic', accessor: 'oleic' },
    { key: 'palmitic', label: 'Palmitic', accessor: 'palmitic' },
    { key: 'ricinoleic', label: 'Ricinoleic', accessor: 'ricinoleic' },
    { key: 'stearic', label: 'Stearic', accessor: 'stearic' },
  ],
  'fa-all': [
    { key: 'sap', label: 'Sap', accessor: 'sap' },
    { key: 'capric', label: 'Capric', accessor: 'capric' },
    { key: 'caprylic', label: 'Caprylic', accessor: 'caprylic' },
    { key: 'docosadienoic', label: 'Docosadienoic', accessor: 'docosadienoic' },
    { key: 'docosenoid', label: 'Docosenoid', accessor: 'docosenoid' },
    { key: 'eicosenoid', label: 'Eicosenoid', accessor: 'eicosenoid' },
    { key: 'eicosenoic', label: 'Eicosenoic', accessor: 'eicosenoic' },
    { key: 'erucic', label: 'Erucic', accessor: 'erucic' },
    { key: 'lauric', label: 'Lauric', accessor: 'lauric' },
    { key: 'linoleic', label: 'Linoleic', accessor: 'linoleic' },
    { key: 'linolenic', label: 'Linolenic', accessor: 'linolenic' },
    { key: 'myristic', label: 'Myristic', accessor: 'myristic' },
    { key: 'oleic', label: 'Oleic', accessor: 'oleic' },
    { key: 'palmitic', label: 'Palmitic', accessor: 'palmitic' },
    { key: 'ricinoleic', label: 'Ricinoleic', accessor: 'ricinoleic' },
    { key: 'stearic', label: 'Stearic', accessor: 'stearic' },
  ],
  social: [
    { key: 'comments', label: 'Comments', accessor: 'comments' },
    { key: 'posts', label: 'Posts', accessor: 'posts' },
    { key: 'recipes', label: 'Recipes', accessor: 'recipes' },
  ],
};

const tabOptions = [
  { key: 'properties', icon: 'bullseye', content: 'Properties' },
  { key: 'saturates', icon: 'tint', content: 'Saturates' },
  { key: 'fa-common', icon: 'bars', content: 'Fatty Acids - Common' },
  { key: 'fa-all', icon: 'bars', content: 'Fatty Acids - All' },
  { key: 'social', icon: 'users', content: 'Social Statistics' },
];

function getOilValue(oil, accessor) {
  // Try direct property, then breakdown, then 0
  return oil[accessor] ?? (oil.breakdown ? oil.breakdown[accessor] : '') ?? '';
}

function Oils() {
  const [search, setSearch] = useState('');
  const [activeTab, setActiveTab] = useState('properties');
  const [sortKey, setSortKey] = useState('name');
  const [sortAsc, setSortAsc] = useState(true);

  const { data: oilsList, isLoading } = useQuery({
    queryKey: ['oils', search],
    queryFn: () => oils.list({ search }),
  });

  const oilsData = Array.isArray(oilsList?.data?.data) ? oilsList.data.data : [];
  const filteredOils = oilsData.filter(oil =>
    oil.name.toLowerCase().includes(search.toLowerCase())
  );
  const sortedOils = [...filteredOils].sort((a, b) => {
    if (sortKey === 'name') {
      return sortAsc
        ? a.name.localeCompare(b.name)
        : b.name.localeCompare(a.name);
    } else {
      return sortAsc
        ? (getOilValue(a, sortKey) || 0) - (getOilValue(b, sortKey) || 0)
        : (getOilValue(b, sortKey) || 0) - (getOilValue(a, sortKey) || 0);
    }
  });

  const renderTable = (tabKey) => (
    <>
      <Input
        icon="search"
        placeholder="Filter Oils..."
        value={search}
        onChange={e => setSearch(e.target.value)}
        fluid
        style={{ marginBottom: 16 }}
      />
      {isLoading ? (
        <Segment loading />
      ) : (
        <Table celled sortable>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell
                sorted={sortKey === 'name' ? (sortAsc ? 'ascending' : 'descending') : null}
                onClick={() => {
                  setSortKey('name');
                  setSortAsc(sortKey === 'name' ? !sortAsc : true);
                }}
              >
                Name
              </Table.HeaderCell>
              {columnsByTab[tabKey].map(col => (
                <Table.HeaderCell
                  key={col.key}
                  sorted={sortKey === col.key ? (sortAsc ? 'ascending' : 'descending') : null}
                  onClick={() => {
                    setSortKey(col.key);
                    setSortAsc(sortKey === col.key ? !sortAsc : true);
                  }}
                >
                  {col.label}
                </Table.HeaderCell>
              ))}
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {sortedOils.map(oil => (
              <Table.Row key={oil.id}>
                <Table.Cell>
                  <Link to={`/oils/${oil.id}`}>{oil.name}</Link>
                </Table.Cell>
                {columnsByTab[tabKey].map(col => (
                  <Table.Cell key={col.key}>{getOilValue(oil, col.accessor)}</Table.Cell>
                ))}
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      )}
    </>
  );

  const panes = [
    {
      menuItem: tabOptions[0],
      render: () => <Tab.Pane style={{ overflowX: 'auto' }}>{renderTable('properties')}</Tab.Pane>,
    },
    {
      menuItem: tabOptions[1],
      render: () => <Tab.Pane style={{ overflowX: 'auto' }}>{renderTable('saturates')}</Tab.Pane>,
    },
    {
      menuItem: tabOptions[2],
      render: () => <Tab.Pane style={{ overflowX: 'auto' }}>{renderTable('fa-common')}</Tab.Pane>,
    },
    {
      menuItem: tabOptions[3],
      render: () => <Tab.Pane style={{ overflowX: 'auto' }}>{renderTable('fa-all')}</Tab.Pane>,
    },
    
  ];

  return (
    <>
      <Helmet>
        <title>Miakara Soap Recipes - Oils Database</title>
      </Helmet>
      <Container>
        <Segment vertical>
          <Header as="h1">Oils Database</Header>
          <Tab
            menu={{ secondary: true, pointing: true }}
            panes={panes}
            activeIndex={tabOptions.findIndex(t => t.key === activeTab)}
            onTabChange={(_, data) => setActiveTab(tabOptions[data.activeIndex].key)}
          />
        </Segment>
      </Container>
    </>
  );
}

export default Oils; 